import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { NotFoundError, RedirectError } from '#src/shared/error';

import type { NewsAndGuidesDisableMode } from '#src/app/pages/newsAndGuides/shared/utils';
import { redirectToPetitionGuidesIfDisabled } from '#src/app/pages/newsAndGuides/shared/utils';
import { getNewGuideUrl } from '#src/app/shared/utils/guides';

import type { NewsAndGuidesDetailsDataQuery, NewsAndGuidesDetailsDataQueryVariables } from './api.graphql';
import { NewsAndGuidesDetailsData } from './api.graphql';

export type NewsAndGuidesDetailsPageData = {
	hasGuides: boolean;
	countryCode: string;
	details: NonNullable<NewsAndGuidesDetailsDataQuery['details']>;
};

export async function getNewsAndGuidesDetailsPageData(
	{ gql: { fetch } }: UtilityContext,
	{
		countryCode,
		locale,
		slug,
		disableMode,
	}: { countryCode: string; locale: string; slug: string; disableMode: NewsAndGuidesDisableMode },
): Promise<NewsAndGuidesDetailsPageData> {
	// Country-disable check runs first: the FCM-driven redirect takes priority over slug-based redirects
	// and avoids a needless fetch.
	redirectToPetitionGuidesIfDisabled(disableMode);

	const { data } = await fetch<NewsAndGuidesDetailsDataQuery, NewsAndGuidesDetailsDataQueryVariables>({
		query: NewsAndGuidesDetailsData,
		variables: { countryCode, locale, slugOrId: slug },
		rejectOnError: true,
	});

	const newGuideUrl = getNewGuideUrl(slug);
	if (newGuideUrl) {
		throw new RedirectError({ url: newGuideUrl, permanent: true });
	}

	if (!data?.details) {
		throw new NotFoundError();
	}

	return {
		hasGuides: !!data.hasGuides,
		countryCode,
		details: data.details,
	};
}
